import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import {
  useCreateServiceType,
  useServiceFormOptions,
  useSetServiceTypeActive,
  useUpdateServiceType,
} from '@/hooks/catalog-admin';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import { centsToInput, parseMoneyInput } from '@shared/money';
import {
  PRICING_UNITS,
  PRICING_UNIT_LABELS,
  type PricingUnit,
  type ServiceTypeView,
} from '@shared/schemas/service-type';

/**
 * Create or edit a service on the rate card.
 *
 * The form lives in a child component so Radix unmounting the closed dialog is
 * what resets it — same reasoning as CategoryFormDialog.
 */
export function ServiceFormDialog({
  open,
  onOpenChange,
  service,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service?: ServiceTypeView;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <ServiceForm
            service={service}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ServiceForm({ service, onDone }: { service?: ServiceTypeView; onDone: () => void }) {
  const toast = useToast();
  const isEdit = service !== undefined;

  const [name, setName] = useState(service?.name ?? '');
  const [description, setDescription] = useState(service?.description ?? '');
  const [pricingUnit, setPricingUnit] = useState<PricingUnit>(service?.pricingUnit ?? 'flat');
  // Held as the string the person typed, converted on submit. Holding cents and
  // reformatting on every keystroke fights anyone typing "12.5" — the cursor
  // jumps the moment it becomes "12.50".
  const [price, setPrice] = useState(centsToInput(service?.defaultPriceCents ?? null));
  const [duration, setDuration] = useState(String(service?.defaultDurationMinutes ?? 120));
  const [isRequestable, setIsRequestable] = useState(service?.isRequestable ?? true);
  const [isBillable, setIsBillable] = useState(service?.isBillable ?? true);
  const [priceError, setPriceError] = useState<string | undefined>(undefined);

  // What this service wants from whoever does it — business-rules.md §4's
  // scoring inputs, added in 7.4.
  const [skillIds, setSkillIds] = useState<string[]>((service?.skills ?? []).map((s) => s.id));
  const [positionIds, setPositionIds] = useState<string[]>(
    (service?.positions ?? []).map((p) => p.id),
  );
  const options = useServiceFormOptions();

  const create = useCreateServiceType();
  const update = useUpdateServiceType(service?.id ?? '');
  const setActive = useSetServiceTypeActive(service?.id ?? '');
  const mutation = isEdit ? update : create;

  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    const money = parseMoneyInput(price);
    if (!money.ok) {
      setPriceError(money.message);
      return;
    }
    setPriceError(undefined);

    mutation.mutate(
      {
        name,
        description,
        pricingUnit,
        defaultPriceCents: money.cents,
        defaultDurationMinutes: Number(duration) || 0,
        isRequestable,
        isBillable,
        skillIds,
        positionIds,
      },
      {
        onSuccess: (saved) => {
          toast.success(isEdit ? `Saved ${saved.name}.` : `Added ${saved.name}.`);
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? `Edit ${service.name}` : 'Add a service'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        This is the rate card. The same entry classifies a job, appears on the client request form,
        and prices an invoice line.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <Field
          label="Name"
          name="name"
          required
          value={name}
          error={fieldErrors.name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          placeholder="Deep Cleaning"
          hint="This is what a client sees. There is no separate code to set."
        />

        <div className="space-y-1.5">
          <Label htmlFor="service-description">Description</Label>
          <textarea
            id="service-description"
            name="description"
            rows={2}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body placeholder:text-ink-500"
          />
        </div>

        <fieldset>
          <legend className="text-label text-ink-900">How it is priced</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {PRICING_UNITS.map((unit) => (
              <button
                key={unit}
                type="button"
                role="radio"
                aria-checked={pricingUnit === unit}
                onClick={() => {
                  setPricingUnit(unit);
                }}
                className={cn(
                  'inline-flex min-h-touch items-center rounded-full px-3.5 text-label',
                  pricingUnit === unit
                    ? 'bg-honey-400 text-ink-900'
                    : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
                )}
              >
                {PRICING_UNIT_LABELS[unit]}
              </button>
            ))}
          </div>
        </fieldset>

        <Field
          label={pricingUnit === 'hourly' ? 'Hourly rate' : 'Price'}
          name="price"
          inputMode="decimal"
          value={price}
          error={priceError ?? fieldErrors.defaultPriceCents}
          onChange={(event) => {
            setPrice(event.target.value);
            setPriceError(undefined);
          }}
          placeholder="125.00"
          // Two different answers, and the field has to allow both. "Other /
          // Custom" is quoted per job; a service included at no charge is 0.
          hint="Leave it empty if this one is quoted each time. Zero means included at no charge."
        />

        <Field
          label="Default length in minutes"
          name="duration"
          type="number"
          min={5}
          max={1440}
          required
          value={duration}
          error={fieldErrors.defaultDurationMinutes}
          onChange={(event) => {
            setDuration(event.target.value);
          }}
          hint="Used to suggest how long to block out when a job is scheduled."
        />

        <fieldset className="rounded-sm border border-ink-300 bg-ink-100/40 p-3">
          <legend className="px-1 text-label text-ink-900">Where it appears</legend>

          <label className="flex min-h-touch items-center gap-2.5">
            <input
              type="checkbox"
              name="isRequestable"
              checked={isRequestable}
              onChange={(event) => {
                setIsRequestable(event.target.checked);
              }}
              className="size-4 shrink-0 accent-honey-500"
            />
            <span className="text-body text-ink-900">On the client request form</span>
          </label>

          <label className="flex min-h-touch items-center gap-2.5">
            <input
              type="checkbox"
              name="isBillable"
              checked={isBillable}
              onChange={(event) => {
                setIsBillable(event.target.checked);
              }}
              className="size-4 shrink-0 accent-honey-500"
            />
            <span className="text-body text-ink-900">In the invoice builder</span>
          </label>

          <p className="mt-1.5 text-meta text-ink-500">
            Separate on purpose — a trip fee belongs on an invoice and not on the request form.
          </p>

          {fieldErrors.isRequestable ? (
            <p role="alert" className="mt-1.5 text-meta text-status-priority-text">
              {fieldErrors.isRequestable}
            </p>
          ) : null}
        </fieldset>

        {/*
          **What this service wants from whoever does it.** business-rules.md §4
          scores "holds a skill the service type wants" (+4 each) and "position
          matches" (+3), and until 7.4 there was nothing saying which — so the
          two link tables and these two pickers arrived together.

          Leaving both empty is normal and is not a gap: the ranking scores
          nothing for them and says so in words, rather than inventing a match.
        */}
        <fieldset className="rounded-sm border border-ink-300 p-3">
          <legend className="px-1 text-label text-ink-900">Who suits this work</legend>
          <p className="mb-2 text-meta text-ink-500">
            Used to rank people when you assign a job. Leave it empty and the ranking simply does
            not score it.
          </p>

          <ChipPicker
            label="Skills it wants"
            options={options.data?.skills ?? []}
            selected={skillIds}
            onToggle={(id) => {
              setSkillIds((current) =>
                current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
              );
            }}
            empty="No skills recorded yet. Add them on an employee's record first."
          />

          <div className="mt-3">
            <ChipPicker
              label="Positions that suit it"
              options={options.data?.positions ?? []}
              selected={positionIds}
              onToggle={(id) => {
                setPositionIds((current) =>
                  current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
                );
              }}
              empty="No positions set up yet — add them under Settings → Positions."
            />
          </div>
        </fieldset>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          {isEdit ? (
            <Button
              variant="outline"
              className="sm:mr-auto"
              disabled={setActive.isPending}
              onClick={() => {
                setActive.mutate(!service.isActive, {
                  onSuccess: (saved) => {
                    toast.success(
                      saved.isActive
                        ? `${saved.name} is back on the list.`
                        : `${saved.name} retired.`,
                    );
                    onDone();
                  },
                  onError: (cause) => {
                    toast.error(
                      cause instanceof ApiError ? cause.message : 'That could not be saved.',
                    );
                  },
                });
              }}
            >
              {service.isActive ? 'Retire this service' : 'Bring it back'}
            </Button>
          ) : null}

          <Dialog.Close asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add service'}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * A row of toggleable chips — the same control the employee form uses for
 * skills, written once here because 7.4 needed a second and a third of it.
 *
 * `role="checkbox"` rather than a styled `<button>` alone: several can be on at
 * once, which is what a checkbox means and what a screen reader needs told.
 */
function ChipPicker({
  label,
  options,
  selected,
  onToggle,
  empty,
}: {
  label: string;
  options: { id: string; name: string }[];
  selected: string[];
  onToggle: (id: string) => void;
  empty: string;
}) {
  return (
    <div>
      <p className="text-label text-ink-900">{label}</p>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {options.map((entry) => {
          const isOn = selected.includes(entry.id);
          return (
            <button
              key={entry.id}
              type="button"
              role="checkbox"
              aria-checked={isOn}
              onClick={() => {
                onToggle(entry.id);
              }}
              className={cn(
                // `min-w-touch` as well as `min-h-touch` — a short chip like
                // "Bond" is exactly the case that failed the 375px sweep once.
                'inline-flex min-h-touch min-w-touch items-center justify-center rounded-full px-3.5 text-label',
                isOn
                  ? 'bg-honey-400 text-ink-900'
                  : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
              )}
            >
              {entry.name}
            </button>
          );
        })}
        {options.length === 0 ? <p className="text-meta text-ink-500">{empty}</p> : null}
      </div>
    </div>
  );
}
